import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { AppInterceptor } from '@services/app.interceptor';
import { provideToastr, ToastrService } from 'ngx-toastr';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import {
  CustomToasterService,
  LOCAL_STORAGE,
  NavigationService,
  TitleService,
  TranslocoHttpLoader,
} from '@core-utils';
import { authCodeFlowConfig } from '@config/auth.config';
import {
  LayoutService,
  MENU_ITEMS_INJECTION,
  PAGE_ROUTES_INJECTION,
  SwitchLanguageService,
  ThemeModeService,
} from '@ui-kit';
import {
  LANDING_URL,
  LOGO_CONFIG,
  PAGE_ROUTES,
  provideCoreDataScope,
} from '@shared/core-data';
import { MenuItemService } from '@pages/auth/services/menu-item.service';
import { switchMap } from 'rxjs';
import {
  KEYCLOAK_CONFIG,
  PermissionService,
  AuthService,
  UserService,
} from '@shared/auth';
import { environment } from '@environments/environment';
import { UsersApiService, OrganizationsApiService } from '@shared/data-access';

function appInitializer(
  authService: AuthService,
  userService: UserService,
  oauthService: OAuthService,
  navigationService: NavigationService,
) {
  return () => {
    return new Promise<void>((resolve) => {
      configureOAuth(oauthService);
      authService.refreshTokenAfterSleep();
      authService.updateExpireToken();
      navigationService.initializeNavigation(authService.hasValidAccessToken());

      oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
        const token = authService.hasValidAccessToken();
        if (token) {
          userService
            .getUserProfile()
            .pipe(
              switchMap(() => navigationService.handleNavigationAfterLogin()),
            )
            .subscribe({
              next: () => resolve(),
            });
        } else {
          localStorage.removeItem(LOCAL_STORAGE.NAVIGATION_URL);
          resolve();
        }
      });
    });
  };
}

function configureOAuth(oauthService: OAuthService) {
  oauthService.configure(authCodeFlowConfig);
  oauthService.setupAutomaticSilentRefresh();
}

export const appConfig: ApplicationConfig = {
  providers: [
    MenuItemService,
    PermissionService,
    ThemeModeService,
    SwitchLanguageService,
    AuthService,
    UsersApiService,
    UserService,
    NavigationService,
    NgbActiveOffcanvas,
    LayoutService,
    TitleService,
    OrganizationsApiService,
    { provide: PAGE_ROUTES_INJECTION, useValue: PAGE_ROUTES },
    { provide: KEYCLOAK_CONFIG, useValue: environment.keycloak },
    {
      provide: LANDING_URL,
      useValue: environment.landingUrl ?? '',
    },
    {
      provide: LOGO_CONFIG,
      useValue: { logoUrl: './assets/media/main/logo.png' },
    },
    {
      provide: MENU_ITEMS_INJECTION,
      useFactory: (menuService: MenuItemService) => menuService.getMenuItems(),
      deps: [MenuItemService],
    },
    OAuthService,
    provideOAuthClient(),
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideToastr(),
    provideHttpClient(withInterceptors([AppInterceptor])),
    importProvidersFrom([BrowserAnimationsModule]),
    provideEnvironmentNgxMask({
      validation: false,
    }),
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr', 'es'],
        defaultLang: SwitchLanguageService.getStoredLanguage(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideCoreDataScope(),
    provideAppInitializer(() => {
      const initializerFn = appInitializer(
        inject(AuthService),
        inject(UserService),
        inject(OAuthService),
        inject(NavigationService),
      );
      return initializerFn();
    }),
    {
      provide: CustomToasterService,
      useClass: CustomToasterService,
      deps: [ToastrService, TranslocoService],
    },
  ],
};
